// ========================= LocalForm.jsx =========================
// Local = airport / railway-station transfers only. One field is the customer's
// location (Google autocomplete), the other is a hub picked from the served-city
// directory (src/data/transferHubs.js). Direction decides which end is pickup vs
// drop. trip_type stays 1 so the results/booking flow is unchanged.
import React, { useEffect, useState, useRef, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { getCabs } from "../../../../../store/Cab/CabAction"
import GooglePlacesAutocomplete from "react-google-places-autocomplete"
import { toast } from "react-toastify"
import { getPlaceDetailsByPlaceId } from "../../../../../utils/placeDetails"
import { getDefaultOptions, getDefaultOptionsSync } from "../../../../../utils/defaultCityOptions"
import { saveRecentSearch, saveRecentTrip } from "../../../../../utils/recentSearches"
import { formatMobileDisplay, parseMobileInput, isValidMobileNumber } from "../../../../../utils/mobileNumberUtils"
import { DatePickerInput, TimePickerInput, DateTimeProvider } from "../../../../../components/DateTime"
import { loadGoogleMaps } from "../../../../../utils/loadGoogleMaps"
import { buildHubOptions } from "../../../../../data/transferHubs"
import { slugify } from "../../../../../utils/slugify"

const placeSelectStyles = {
  control: (p, state) => ({
    ...p,
    padding: "0 6px",
    border: state.isFocused ? "1.5px solid #21a5a0" : "1.5px solid #e6ecea",
    borderRadius: "1.2rem",
    background: state.isFocused ? "#ffffff" : "#f5f9f8",
    boxShadow: state.isFocused ? "0 0 0 4px #e9f6f5" : "none",
    width: "100%",
    height: "4.4rem",
    minHeight: "4.4rem",
    display: "flex",
    alignItems: "center",
    marginBottom: "1.4rem",
    fontSize: "1.4rem",
    transition: "border-color .18s, box-shadow .18s, background .18s",
  }),
  input: (p) => ({ ...p, color: "#152321", fontSize: "1.4rem" }),
  placeholder: (p) => ({ ...p, color: "#71817f", fontSize: "1.4rem" }),
  menu: (p) => ({ ...p, fontSize: "1.4rem", zIndex: 3, borderRadius: "1.2rem", overflow: "hidden" }),
  option: (p, s) => ({ ...p, fontSize: "1.4rem", backgroundColor: s.isSelected ? "#e9f6f5" : "white", color: "#152321" }),
}

const LocalForm = () => {
  useEffect(() => {
    loadGoogleMaps()
  }, [])

  const [formValues, setFormValues] = useState({
    place: null,
    pickUpDate: "",
    pickUpTime: "",
    mobileNumber: "",
  })

  // transferMode: which hub. direction: "to" = going to the hub (hub is drop),
  // "from" = coming from the hub (hub is pickup).
  const [transferMode, setTransferMode] = useState("airport")
  const [direction, setDirection] = useState("to")
  const [hubValue, setHubValue] = useState("")

  const [placeDetails, setPlaceDetails] = useState({ lat: null, long: null, city: "", fullAddress: "" })
  const [defaultFromOptions, setDefaultFromOptions] = useState(() => getDefaultOptionsSync("from"))

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { location } = useSelector((state) => state.location)
  const [searchLoading, setSearchLoading] = useState(false)

  const placeSelectRef = useRef(null)
  const mobileNumberInputRef = useRef(null)
  const pickUpDateRef = useRef(null)
  const pickUpTimeRef = useRef(null)

  const today = new Date()
  const formattedToday = today.toLocaleDateString("en-CA")

  const extractCityFromText = (text = "") => (text.split(",")[0] || "").trim()
  const getLabel = (v) => (!v ? "" : typeof v === "string" ? v : v?.fullAddress || v?.label || "")

  // A local transfer is within ONE city (rider's address <-> that city's
  // airport/station). Once the rider's location resolves to a served city, show
  // only that city's hubs instead of listing every metro. Falls back to all
  // cities when the city is not one we serve, or nothing is picked yet.
  const hubGroups = useMemo(() => {
    const all = buildHubOptions(transferMode)
    const cityStr = (placeDetails.city || "").trim().toLowerCase()
    const addrStr = (placeDetails.fullAddress || "").toLowerCase()
    if (!cityStr && !addrStr) return all
    const citySlug = cityStr ? slugify(cityStr) : null
    const matched = all.filter((g) => {
      const hub0 = g.options[0]?.hub
      if (!hub0) return false
      const name = (hub0.cityName || "").toLowerCase()
      return (
        (citySlug && hub0.cityKey === citySlug) ||
        (name && (cityStr === name || addrStr.includes(name)))
      )
    })
    return matched.length ? matched : all
  }, [transferMode, placeDetails.city, placeDetails.fullAddress])
  const hubLookup = useMemo(() => {
    const m = {}
    hubGroups.forEach((g) => g.options.forEach((o) => { m[o.value] = o.hub }))
    return m
  }, [hubGroups])
  const selectedHub = hubValue ? hubLookup[hubValue] : null

  const hubLabel = transferMode === "airport" ? "airport" : "railway station"
  const hubShort = transferMode === "airport" ? "airport" : "station"

  // Reset the hub when the transfer mode changes (options differ).
  useEffect(() => {
    setHubValue("")
  }, [transferMode])

  // If the filtered hub list no longer includes the chosen hub (the rider's
  // city changed), clear it so a hub from another city is never submitted.
  useEffect(() => {
    if (hubValue && !hubLookup[hubValue]) setHubValue("")
  }, [hubLookup, hubValue])

  // No city autofill for transfers: the pickup must be a precise, coordinate
  // backed address (Google place) so the distance-based fare is exact. The
  // customer picks their exact location from the suggestions.

  useEffect(() => {
    const load = async () => {
      const opts = await getDefaultOptions("from", location?.city)
      setDefaultFromOptions(opts || [])
    }
    load()
  }, [location?.city])

  const makeValueWithDetails = (value, details) => {
    const labelText = getLabel(value)
    return {
      ...value,
      latitude: details?.lat ?? null,
      longitude: details?.lng ?? null,
      city: details?.city || extractCityFromText(labelText),
      fullAddress: details?.fullAddress || labelText,
    }
  }

  const handlePlaceSelect = async (value) => {
    if (!value) {
      setFormValues((prev) => ({ ...prev, place: null }))
      setPlaceDetails({ lat: null, long: null, city: "", fullAddress: "" })
      return
    }
    const placeId = value?.value?.place_id || value?.place_id
    const details = placeId ? await getPlaceDetailsByPlaceId(placeId) : null
    const nextValue = makeValueWithDetails(value, details)
    setFormValues((prev) => ({ ...prev, place: nextValue }))
    saveRecentSearch(nextValue, "from")
    setPlaceDetails({ lat: nextValue.latitude, long: nextValue.longitude, city: nextValue.city, fullAddress: nextValue.fullAddress })
    setTimeout(() => pickUpDateRef.current?.open?.(), 150)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formValues.place) return toast.error("Please enter your location")
    // Transfers are priced by distance, so the customer's end must be a precise
    // coordinate-backed address (a picked Google place), not a typed city.
    const placeHasCoords =
      Number.isFinite(Number(placeDetails.lat)) &&
      Number.isFinite(Number(placeDetails.long)) &&
      Number(placeDetails.lat) !== 0 &&
      Number(placeDetails.long) !== 0
    if (!placeHasCoords) {
      return toast.error("Please pick your exact location from the suggestions so we can calculate the fare")
    }
    if (!hubValue || !selectedHub) return toast.error(`Please select the ${hubLabel}`)
    if (!formValues.pickUpDate) return toast.error("Please select pickup date")
    if (!formValues.pickUpTime) return toast.error("Please select pickup time")
    if (!formValues.mobileNumber) return toast.error("Please enter mobile number")
    if (!isValidMobileNumber(formValues.mobileNumber)) return toast.error("Enter a valid 10-digit mobile number (cannot start with 0)")

    const place = {
      address: placeDetails.fullAddress || getLabel(formValues.place) || "",
      lat: placeDetails.lat,
      long: placeDetails.long,
      city: placeDetails.city || formValues.place?.city || extractCityFromText(getLabel(formValues.place)) || "",
    }
    const hub = {
      address: selectedHub.fullAddress,
      lat: selectedHub.lat,
      long: selectedHub.lng,
      city: selectedHub.cityName,
    }

    // direction "to": pickup = your location, drop = hub. "from": reversed.
    const pickupEnd = direction === "to" ? place : hub
    const dropEnd = direction === "to" ? hub : place

    if (!pickupEnd.city || !dropEnd.city) return toast.error("Please select a valid location")

    const tripData = {
      trip_type: 1,
      round_trip: 0,
      pickup: pickupEnd.address,
      drop: dropEnd.address,
      pickup_lat: pickupEnd.lat,
      pickup_long: pickupEnd.long,
      drop_lat: dropEnd.lat,
      drop_long: dropEnd.long,
      pickup_city: pickupEnd.city,
      drop_city: dropEnd.city,
      package_type_in_hours: "",
      trip_date: formValues.pickUpDate,
      trip_time: formValues.pickUpTime,
      phone_number: formValues.mobileNumber,
      ip_address: location?.ip || "8425",
    }

    setSearchLoading(true)
    const safetyTimeout = setTimeout(() => setSearchLoading(false), 15000)

    try {
      const response = await dispatch(getCabs(tripData)).unwrap()
      clearTimeout(safetyTimeout)

      localStorage.setItem("tripData", JSON.stringify(tripData))
      localStorage.setItem("cabSearchResult", JSON.stringify(response?.data ?? {}))
      window.dispatchEvent(new Event("tripDataUpdated"))

      const q = (k, v) => (v === null || v === undefined || v === "" ? "" : `${k}=${encodeURIComponent(v)}`)
      const params = [
        "trip_type=1",
        "round_trip=0",
        q("pickup", tripData.pickup),
        q("drop", tripData.drop),
        q("pickup_lat", tripData.pickup_lat),
        q("pickup_long", tripData.pickup_long),
        q("drop_lat", tripData.drop_lat),
        q("drop_long", tripData.drop_long),
        q("pickup_city", tripData.pickup_city),
        q("drop_city", tripData.drop_city),
        q("trip_date", tripData.trip_date),
        q("trip_time", tripData.trip_time),
        q("phone_number", tripData.phone_number),
      ].filter(Boolean)

      saveRecentTrip({ trip_type: 1, fromCity: tripData.pickup_city, toCity: tripData.drop_city })
      navigate(`/cabs?${params.join("&")}`)
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.message || "Something went wrong"
      const isRateLimit = typeof msg === "string" && (msg.includes("Rate limit") || msg.includes("Maximum"))
      toast.error(isRateLimit ? "You've exceeded the request limit. Please try again after a few hours." : msg)
    } finally {
      setSearchLoading(false)
    }
  }

  const placePlaceholder = direction === "to" ? "Your exact pick-up address" : "Your exact drop address"

  const placeField = (
    <GooglePlacesAutocomplete
      key="local-place"
      debounce={400}
      minLengthAutocomplete={3}
      selectProps={{
        ref: placeSelectRef,
        value: formValues.place,
        onChange: (v) => handlePlaceSelect(v),
        placeholder: placePlaceholder,
        isClearable: true,
        defaultOptions: defaultFromOptions,
        getOptionLabel: (e) => e?.label || "",
        getOptionValue: (e) => e?.value?.place_id || e?.place_id || e?.value?.description || e?.label || "",
        styles: placeSelectStyles,
        components: { DropdownIndicator: () => null, IndicatorSeparator: () => null },
      }}
      autocompletionRequest={{ componentRestrictions: { country: "in" } }}
    />
  )

  const hubField = (
    <div className="local-form__hub" key="local-hub">
      <select
        className={`local-form__hub-select${hubValue ? " has-value" : ""}`}
        value={hubValue}
        onChange={(e) => setHubValue(e.target.value)}
        aria-label={transferMode === "airport" ? "Select airport and terminal" : "Select railway station"}
      >
        <option value="">
          {transferMode === "airport" ? "Select airport & terminal" : "Select railway station"}
        </option>
        {hubGroups.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  )

  return (
    <div className="local-form">
      <form onSubmit={handleSubmit}>
        {/* Two clearly distinct levels so the choice never reads as one mushy
            block: (1) transfer type = prominent teal cards + shimmer on active,
            (2) direction = a lighter coral-accented sub-toggle. Each is labelled. */}
        <div className="local-form__modes">
          <div className="local-form__field">
            <span className="local-form__seg-label">Transfer type</span>
            <div className="local-form__seg local-form__seg--mode" role="tablist" aria-label="Transfer type">
              <button
                type="button"
                role="tab"
                aria-selected={transferMode === "airport"}
                className={`local-form__seg-btn${transferMode === "airport" ? " is-active" : ""}`}
                onClick={() => setTransferMode("airport")}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M17.8 19.2 16 11l3.5-3.5a2.1 2.1 0 0 0-3-3L13 8 4.8 6.2a.5.5 0 0 0-.5.8L8 11l-3 3H2l2 3 3 2 1-3 3-3 3.9 3.7a.5.5 0 0 0 .8-.5Z" />
                </svg>
                Airport
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={transferMode === "railway"}
                className={`local-form__seg-btn${transferMode === "railway" ? " is-active" : ""}`}
                onClick={() => setTransferMode("railway")}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="6" y="4" width="12" height="12" rx="2" /><path d="M6 10h12M9 20l-2 2M15 20l2 2" /><circle cx="9" cy="13" r="0.6" /><circle cx="15" cy="13" r="0.6" />
                </svg>
                Railway station
              </button>
            </div>
          </div>

          <div className="local-form__field">
            <span className="local-form__seg-label">Direction</span>
            <div className="local-form__seg local-form__seg--dir" role="tablist" aria-label="Direction">
              <button
                type="button"
                role="tab"
                aria-selected={direction === "to"}
                className={`local-form__seg-btn${direction === "to" ? " is-active" : ""}`}
                onClick={() => setDirection("to")}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                </svg>
                To the {hubShort}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={direction === "from"}
                className={`local-form__seg-btn${direction === "from" ? " is-active" : ""}`}
                onClick={() => setDirection("from")}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M19 12H5" /><path d="m12 19-7-7 7-7" />
                </svg>
                From the {hubShort}
              </button>
            </div>
          </div>
        </div>

        {/* Pickup on top, drop below, ordered by direction. */}
        {direction === "to" ? (
          <>
            {placeField}
            {hubField}
          </>
        ) : (
          <>
            {hubField}
            {placeField}
          </>
        )}

        <DateTimeProvider>
          <div className="local-form__date-time mb-5">
            <DatePickerInput
              ref={pickUpDateRef}
              value={formValues.pickUpDate ? new Date(formValues.pickUpDate) : null}
              minDate={new Date(formattedToday)}
              placeholder="Pick Up Date"
              onChange={(d) => {
                setFormValues((prev) => ({ ...prev, pickUpDate: d ? new Date(d).toLocaleDateString("en-CA") : "" }))
                setTimeout(() => pickUpTimeRef.current?.open?.(), 200)
              }}
            />
            <TimePickerInput
              ref={pickUpTimeRef}
              value={formValues.pickUpTime}
              placeholder="Pick Up Time"
              selectedDate={formValues.pickUpDate ? new Date(formValues.pickUpDate) : null}
              onChange={(t) => {
                setFormValues((prev) => ({ ...prev, pickUpTime: t || "" }))
                setTimeout(() => mobileNumberInputRef.current?.focus?.(), 150)
              }}
            />
          </div>
        </DateTimeProvider>

        <div className="input-phone-wrapper mb-5">
          <div
            className={`input-phone-box${isValidMobileNumber(formValues.mobileNumber) ? " input-phone-box--valid" : formValues.mobileNumber.length > 0 ? " input-phone-box--invalid" : ""}`}
          >
            <span className="phone-code">+91</span>
            <span className="phone-divider"></span>
            <input
              type="tel"
              className="phone-input"
              placeholder="Mobile Number"
              name="mobileNumber"
              value={formatMobileDisplay(formValues.mobileNumber)}
              onChange={(e) => {
                const v = parseMobileInput(e.target.value)
                setFormValues((p) => ({ ...p, mobileNumber: v }))
              }}
              maxLength={11}
              ref={mobileNumberInputRef}
              required
            />
          </div>
        </div>

        <button type="submit" className="input-submit-button" disabled={searchLoading}>
          {searchLoading ? "Searching..." : "Search"}
        </button>
      </form>

      <p className="local-form__paragraph">
        Fixed-price airport &amp; railway station transfers. Pick your exact address from the suggestions for an accurate fare.
      </p>
    </div>
  )
}

export default LocalForm
